import { defineStore } from 'pinia';

// App-level unread-conversations counter for the sidebar "Messages" badge (spec §8.1 AC9 — "'New demand'
// and 'new message' events surface an unread badge"). The source of truth is the SET of CometChat group
// guids that carry unread messages: read/unread actions mutate it OPTIMISTICALLY, so the badge reacts to
// the action itself — instantly, with no server round-trip and immune to the aggregate's own lag — while
// `refresh()` reconciles the set against the server on init / window focus / a new incoming message. It
// lives at app level (started from the authenticated shell once the CometChat session is ready) so the badge
// stays live on every page. The tenant's CometChat user only belongs to marketplace demand groups, so every
// group is a demand conversation — no per-group metadata filtering is needed.
const LISTENER_ID = 'messages-unread-badge';

export const useMessagesUnreadStore = defineStore('messages-unread', () => {
    const unreadGuids = reactive(new Set<string>());
    const count = computed<number>(() => unreadGuids.size);
    const hasUnread = computed<boolean>(() => unreadGuids.size > 0);

    // Guards double start/stop (the session-status watcher can fire repeatedly).
    let started = false;
    // Monotonic token so an out-of-order `refresh()` can't clobber a fresher reconciliation (a stale focus
    // refresh resolving after a newer one) — TDM-587 review.
    let refreshSeq = 0;

    // Stamps the last optimistic mutation per guid. A `refresh()` fetches a server snapshot taken BEFORE it
    // resolves, so a thread the pro reads/marks while that request is in flight would otherwise be resurrected
    // (or dropped) by stale data — the "sometimes the badge counts the thread I'm standing on" flakiness of
    // the TDM-587 review. Guids stamped after a refresh started keep their optimistic value.
    let mutationSeq = 0;
    const mutatedAt = new Map<string, number>();

    // Optimistic, instant — the badge updates the moment a thread is read/unread, not when the server
    // aggregate catches up. `Set.delete` returns whether the guid was actually unread, so the caller can
    // roll back precisely if the server write fails.
    const markGroupRead = (guid: string): boolean => {
        if (!guid) return false;
        mutatedAt.set(guid, ++mutationSeq);
        return unreadGuids.delete(guid);
    };
    const markGroupUnread = (guid: string): void => {
        if (!guid) return;
        mutatedAt.set(guid, ++mutationSeq);
        unreadGuids.add(guid);
    };

    /** Whether a group carries unread messages. The ONE source of truth for both the badge and the
     *  conversation-list dot (TDM-587 review): the UIKit mutates its own conversations' unread counts, so
     *  reading those made the list disagree with the badge. */
    const isGroupUnread = (guid: string): boolean => (guid ? unreadGuids.has(guid) : false);

    // Reconcile the set against the server (getUnreadMessageCountForAllGroups → guids with unread > 0).
    // The server is the truth for every guid EXCEPT the ones the pro touched while this request was in
    // flight: their snapshot predates the action, so applying it blindly would resurrect a just-read thread
    // (deep-linking onto a thread reads it, while the boot `refresh()` is still resolving — TDM-587 review).
    const refresh = async (): Promise<void> => {
        const seq = ++refreshSeq;
        const snapshotAt = mutationSeq; // anything stamped after this is fresher than the data we fetch below
        try {
            const { CometChat } = await import('@cometchat/chat-sdk-javascript');
            const perGroup = (await CometChat.getUnreadMessageCountForAllGroups()) as Record<string, number>;
            if (seq !== refreshSeq) return; // superseded by a newer refresh — its data is fresher
            const serverUnread = new Set(
                Object.entries(perGroup ?? {})
                    .filter(([, unread]) => Number(unread) > 0)
                    .map(([guid]) => guid)
            );
            const isOptimisticallyFresher = (guid: string): boolean => (mutatedAt.get(guid) ?? 0) > snapshotAt;
            for (const guid of [...unreadGuids]) {
                if (!serverUnread.has(guid) && !isOptimisticallyFresher(guid)) unreadGuids.delete(guid);
            }
            for (const guid of serverUnread) {
                if (!isOptimisticallyFresher(guid)) unreadGuids.add(guid);
            }
            // Reconciled stamps can go — keeps the map bounded by the threads touched since this refresh.
            for (const [guid, at] of mutatedAt) {
                if (at <= snapshotAt) mutatedAt.delete(guid);
            }
        } catch (error) {
            // Non-fatal: keep the current set. A later focus / message event retries. Logged rather than
            // swallowed — a silent failure here is exactly what hid the broken listener boot (TDM-587).
            console.error('[CometChat] Unread refresh failed', error);
        }
    };

    // Reading can happen on another device, so refocusing the tab is a cheap moment to resync.
    const onFocus = (): void => {
        refresh();
    };

    // Callers fire this from a status watcher (no await), so it must never reject: an unhandled rejection
    // here is invisible AND leaves `started` latched, killing the badge for the whole session — that is
    // exactly how the listener-boot bug hid itself (TDM-587 review). On failure we unlatch so the next
    // status flip / tenant switch can retry.
    const startTracking = async (): Promise<void> => {
        if (started) return;
        started = true;
        try {
            const { CometChat } = await import('@cometchat/chat-sdk-javascript');
            // A new demand / reply changes unread server-side (and the server excludes a conversation being
            // viewed, which the UIKit auto-reads) — reconcile from it rather than guess the active thread here.
            // `MessageListener` is a CometChat STATIC, not a package export — the named import is undefined at
            // runtime (`new MessageListener()` → TypeError), the same type-only re-export trap as in
            // cometchat-init.ts. That throw silently stopped the badge from ever reconciling (TDM-587).
            CometChat.addMessageListener(
                LISTENER_ID,
                new CometChat.MessageListener({
                    onTextMessageReceived: () => refresh(),
                    onMediaMessageReceived: () => refresh(),
                    onCustomMessageReceived: () => refresh(),
                })
            );
            window.addEventListener('focus', onFocus);
            await refresh();
        } catch (error) {
            started = false;
            window.removeEventListener('focus', onFocus);
            console.error('[CometChat] Unread tracking failed to start', error);
        }
    };

    const stopTracking = async (): Promise<void> => {
        if (!started) return;
        started = false;
        window.removeEventListener('focus', onFocus);
        unreadGuids.clear();
        mutatedAt.clear();
        const { CometChat } = await import('@cometchat/chat-sdk-javascript');
        CometChat.removeMessageListener(LISTENER_ID);
    };

    return { count, hasUnread, isGroupUnread, markGroupRead, markGroupUnread, refresh, startTracking, stopTracking };
});
